import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch, parseJsonSafe } from '../utils/api';

const weekdayOptions = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' }
];

const defaultSettings = {
  autoEnabled: true,
  timezone: 'Europe/Madrid',
  weekdays: [1, 2, 3, 4, 5],
  inStart: '09:56',
  inEnd: '10:00',
  outStart: '13:58',
  outEnd: '14:03',
  pauses: []
};

const MAX_PAUSES = 3;

// Pausa nueva por defecto: comida 14:00-14:05 → vuelta 14:55-15:00
const emptyPause = () => ({ outStart: '14:00', outEnd: '14:05', inStart: '14:55', inEnd: '15:00' });

// Lee el id del usuario del JWT (payload base64) para distinguir las jornadas propias.
const decodeUserId = (token) => {
  try {
    const payload = JSON.parse(atob(String(token).split('.')[1]));
    return payload?.id ?? null;
  } catch (err) {
    return null;
  }
};

// Convierte un timestamp ISO a valor para <input type="datetime-local"> en hora local.
const toLocalInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Hora corta HH:mm (hora local del navegador).
const fmtClock = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Minutos -> "Hh Mm".
const minutesToHm = (m) => {
  const n = Number(m) || 0;
  return `${Math.floor(n / 60)}h ${String(n % 60).padStart(2, '0')}m`;
};

// Lista de sesiones de un día como "09:00–13:00, 14:00–18:00".
const sessionsSummary = (entry) => {
  const list = Array.isArray(entry?.sessions) ? entry.sessions : [];
  if (!list.length) return entry?.clock_in_at ? `${fmtClock(entry.clock_in_at)}–${entry.clock_out_at ? fmtClock(entry.clock_out_at) : '…'}` : '—';
  return list.map((s) => `${fmtClock(s.clock_in_at)}–${s.clock_out_at ? fmtClock(s.clock_out_at) : '…'}`).join(', ');
};

// Convierte un timestamp a 'yyyy-MM-ddTHH:mm' para <input datetime-local> en una ZONA concreta (la de la
// jornada), de modo que el round-trip con el backend (que lo reinterpreta en esa zona) sea simétrico y no
// desplace la hora si el navegador está en otra zona.
const toZonedInput = (value, tz) => {
  if (!value) return '';
  try {
    const parts = new Intl.DateTimeFormat('sv-SE', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false
    }).formatToParts(new Date(value));
    const g = (t) => parts.find((p) => p.type === t)?.value;
    return `${g('year')}-${g('month')}-${g('day')}T${g('hour')}:${g('minute')}`;
  } catch (err) {
    return toLocalInput(value);
  }
};

// Sesiones de un día -> filas editables del formulario de corrección (en la zona de la jornada).
const sessionsToForm = (entry, tz) => {
  const list = Array.isArray(entry?.sessions) ? entry.sessions : [];
  if (!list.length) {
    return entry?.clock_in_at
      ? [{ clockInAt: toZonedInput(entry.clock_in_at, tz), clockOutAt: toZonedInput(entry.clock_out_at, tz) }]
      : [{ clockInAt: '', clockOutAt: '' }];
  }
  return list.map((s) => ({ clockInAt: toZonedInput(s.clock_in_at, tz), clockOutAt: toZonedInput(s.clock_out_at, tz) }));
};

const WorkdayPage = () => {
  const token = localStorage.getItem('token');
  const currentUserId = useMemo(() => decodeUserId(token), [token]);
  const [status, setStatus] = useState(null);
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ from: '', to: '', user_id: '', status: '' });
  const [pageInfo, setPageInfo] = useState({ total: 0, page: 1, pageSize: 50 });
  const [totals, setTotals] = useState({ days: 0, workedMinutes: 0 });
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [exporting, setExporting] = useState('');
  const [rowBusy, setRowBusy] = useState(null);
  const [correcting, setCorrecting] = useState(null);
  const [correctForm, setCorrectForm] = useState({ sessions: [{ clockInAt: '', clockOutAt: '' }], reason: '' });
  const [savingCorrection, setSavingCorrection] = useState(false);
  const [auditFor, setAuditFor] = useState(null);
  const [auditItems, setAuditItems] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [settings, setSettings] = useState(defaultSettings);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsEditable, setSettingsEditable] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userEmail');
    window.location.href = '/login';
  };

  const fetchStatus = async () => {
    setLoadingStatus(true);
    try {
      const res = await apiFetch('/api/workday/me/status', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando estado de jornada');
      setStatus(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingStatus(false);
    }
  };

  const fetchEntries = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: '1', pageSize: '50' });
      if (filters.from) params.append('from', filters.from);
      if (filters.to) params.append('to', filters.to);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.status) params.append('status', filters.status);
      const res = await apiFetch(`/api/workday/entries?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando jornadas');
      setEntries(Array.isArray(data.items) ? data.items : []);
      setTotals({ days: Number(data.totals?.days || 0), workedMinutes: Number(data.totals?.workedMinutes || 0) });
      setPageInfo({ total: Number(data.total || 0), page: Number(data.page || 1), pageSize: Number(data.pageSize || 50) });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await apiFetch('/api/users', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.ok && Array.isArray(data)) setUsers(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await apiFetch('/api/workday/settings', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (res.status === 403) {
        setSettingsEditable(false);
        return;
      }
      if (!res.ok) throw new Error(data.message || 'Error cargando configuración de auto-fichaje');
      setSettingsEditable(true);
      setSettings({
        autoEnabled: data.autoEnabled === true,
        timezone: data.timezone || 'Europe/Madrid',
        weekdays: Array.isArray(data.weekdays) ? data.weekdays : [1, 2, 3, 4, 5],
        inStart: data.inStart || '09:56',
        inEnd: data.inEnd || '10:00',
        outStart: data.outStart || '13:58',
        outEnd: data.outEnd || '14:03',
        pauses: Array.isArray(data.pauses) ? data.pauses : []
      });
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchEntries();
    fetchUsers();
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.from, filters.to, filters.user_id, filters.status]);

  const runAction = async (path, action) => {
    setActionLoading(action);
    setError('');
    setMessage('');
    try {
      const res = await apiFetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({})
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo registrar');
      setMessage(action === 'in' ? 'Entrada registrada' : 'Salida registrada');
      await fetchStatus();
      await fetchEntries();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading('');
    }
  };

  const confirmEntry = async (entry) => {
    setRowBusy(`confirm-${entry.id}`);
    setError('');
    setMessage('');
    try {
      const res = await apiFetch(`/api/workday/entries/${entry.id}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({})
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo confirmar la jornada');
      setMessage('Jornada confirmada');
      await fetchStatus();
      await fetchEntries();
    } catch (err) {
      setError(err.message);
    } finally {
      setRowBusy(null);
    }
  };

  const openCorrection = (entry) => {
    setCorrecting(entry);
    setCorrectForm({ sessions: sessionsToForm(entry, status?.settings?.timezone || 'Europe/Madrid'), reason: '' });
  };

  const updateSession = (idx, field, value) => setCorrectForm((p) => ({
    ...p,
    sessions: p.sessions.map((s, i) => (i === idx ? { ...s, [field]: value } : s))
  }));
  const addSession = () => setCorrectForm((p) => ({ ...p, sessions: [...p.sessions, { clockInAt: '', clockOutAt: '' }] }));
  const removeSession = (idx) => setCorrectForm((p) => ({
    ...p,
    sessions: p.sessions.length > 1 ? p.sessions.filter((_, i) => i !== idx) : p.sessions
  }));

  const submitCorrection = async (e) => {
    e.preventDefault();
    if (!correcting) return;
    if (!correctForm.reason.trim()) {
      setError('Indica el motivo de la corrección');
      return;
    }
    if (!correctForm.sessions.some((s) => s.clockInAt)) {
      setError('Indica al menos una sesión con su hora de entrada');
      return;
    }
    setSavingCorrection(true);
    setError('');
    setMessage('');
    try {
      const res = await apiFetch(`/api/workday/entries/${correcting.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          // Hora de pared 'yyyy-MM-ddTHH:mm'; el backend la interpreta en la zona de la jornada (Europe/Madrid).
          sessions: correctForm.sessions
            .filter((s) => s.clockInAt)
            .map((s) => ({ clockInAt: s.clockInAt, clockOutAt: s.clockOutAt || null })),
          reason: correctForm.reason.trim()
        })
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo corregir la jornada');
      setMessage('Jornada corregida');
      setCorrecting(null);
      await fetchStatus();
      await fetchEntries();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingCorrection(false);
    }
  };

  const exportRegistry = async (format) => {
    setExporting(format);
    setError('');
    try {
      const params = new URLSearchParams({ format });
      if (filters.from) params.append('from', filters.from);
      if (filters.to) params.append('to', filters.to);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.status) params.append('status', filters.status);
      const res = await apiFetch(`/api/workday/export?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error('No se pudo exportar el registro');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'pdf' ? 'registro-jornada.pdf' : 'registro-jornada.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting('');
    }
  };

  const openAudit = async (entry) => {
    setAuditFor(entry);
    setAuditItems([]);
    setLoadingAudit(true);
    try {
      const res = await apiFetch(`/api/workday/entries/${entry.id}/audit`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo cargar el historial');
      setAuditItems(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setError(err.message);
      setAuditFor(null);
    } finally {
      setLoadingAudit(false);
    }
  };

  const fieldLabel = (f) => (f === 'clock_in_at' ? 'Entrada' : f === 'clock_out_at' ? 'Salida' : f === 'status' ? 'Estado' : f === 'sesiones' ? 'Sesiones' : f);
  const auditValue = (field, value) => (['clock_in_at', 'clock_out_at'].includes(field) ? fmtDateTime(value) : (value || '-'));
  const sourceLabel = (s) => (s === 'manual' ? 'Manual' : s === 'auto' ? 'Automático' : s === 'confirm' ? 'Confirmación' : s === 'correccion' ? 'Corrección' : s || '-');

  const saveSettings = async (e) => {
    e.preventDefault();
    if (!settings.weekdays.length) {
      setError('Selecciona al menos un día para auto-fichaje');
      return;
    }
    setSavingSettings(true);
    setError('');
    setMessage('');
    try {
      const res = await apiFetch('/api/workday/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(settings)
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar la configuración');
      setSettings({
        autoEnabled: data.autoEnabled === true,
        timezone: data.timezone || settings.timezone,
        weekdays: Array.isArray(data.weekdays) ? data.weekdays : settings.weekdays,
        inStart: data.inStart || settings.inStart,
        inEnd: data.inEnd || settings.inEnd,
        outStart: data.outStart || settings.outStart,
        outEnd: data.outEnd || settings.outEnd,
        pauses: Array.isArray(data.pauses) ? data.pauses : settings.pauses
      });
      setMessage('Configuración de auto-fichaje guardada');
      setSettingsOpen(false);
      await fetchStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingSettings(false);
    }
  };

  const currentState = useMemo(() => {
    if (status?.active) return 'en_jornada';
    if (status?.today?.clock_out_at) return 'cerrada';
    if (status?.today?.clock_in_at) return 'abierta';
    return 'sin_fichar';
  }, [status]);

  const fmtDateTime = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString();
  };

  const fmtDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString();
  };

  return (
    <div className="small">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h2 className="mb-0">Jornada laboral</h2>
        {settingsEditable && (
          <button className="btn btn-outline-secondary btn-sm" onClick={() => setSettingsOpen((v) => !v)}>
            {settingsOpen ? 'Ocultar configuración' : 'Configurar auto-fichaje'}
          </button>
        )}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      {status?.today?.status === 'propuesta' && (
        <div className="alert alert-warning d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <strong>Tu jornada de hoy se ha registrado automáticamente.</strong>{' '}
            Revísala y <b>confírmala</b> o <b>corrígela</b> para que sea válida.
            <div className="small">
              Entrada: {fmtDateTime(status.today.clock_in_at)} · Salida: {fmtDateTime(status.today.clock_out_at)}
            </div>
          </div>
          <div className="d-flex gap-2">
            {status.today.clock_in_at && Number(status.today.open_count || 0) === 0 ? (
              <button className="btn btn-sm btn-success" disabled={rowBusy === `confirm-${status.today.id}`} onClick={() => confirmEntry(status.today)}>
                {rowBusy === `confirm-${status.today.id}` ? 'Confirmando...' : 'Confirmar'}
              </button>
            ) : (
              <span className="small text-muted align-self-center">Hay una sesión sin cerrar: ficha salida o corrígela para confirmar</span>
            )}
            <button className="btn btn-sm btn-outline-secondary" onClick={() => openCorrection(status.today)}>Corregir</button>
          </div>
        </div>
      )}

      {status?.pendingProposals > 0 && (
        <div className="alert alert-info py-2 small">
          Tienes <strong>{status.pendingProposals}</strong> jornada(s) pendiente(s) de confirmar.{' '}
          <button className="btn btn-link btn-sm p-0 align-baseline" onClick={() => setFilters((p) => ({ ...p, status: 'propuesta', user_id: '' }))}>
            Ver pendientes
          </button>
        </div>
      )}

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          {loadingStatus ? <p className="mb-0">Cargando estado...</p> : (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <div className="text-muted">Estado actual</div>
                <div className="fw-bold">
                  {currentState === 'en_jornada' ? 'En jornada' : currentState === 'cerrada' ? 'Jornada cerrada' : currentState === 'abierta' ? 'Entrada registrada' : 'Sin fichar'}
                  {status?.today?.status === 'propuesta' && <span className="badge bg-warning text-dark ms-2">Sin confirmar</span>}
                  {status?.today?.status === 'confirmada' && <span className="badge bg-success ms-2">Confirmada</span>}
                </div>
                <div className="small text-muted">
                  Hoy: {sessionsSummary(status?.today)} · Trabajado: <strong>{minutesToHm(status?.today?.worked_minutes)}</strong>
                </div>
              </div>
              <div className="d-flex flex-column align-items-end gap-1">
                <div className="d-flex gap-2">
                  <button className="btn btn-success" disabled={actionLoading === 'in' || Boolean(status?.active)} onClick={() => runAction('/api/workday/clock-in', 'in')}>
                    {actionLoading === 'in' ? 'Registrando...' : (status?.today?.clock_in_at && !status?.active ? 'Reanudar' : 'Fichar entrada')}
                  </button>
                  <button className="btn btn-danger" disabled={actionLoading === 'out' || !status?.active} onClick={() => runAction('/api/workday/clock-out', 'out')}>
                    {actionLoading === 'out' ? 'Registrando...' : 'Fichar salida / pausa'}
                  </button>
                </div>
                <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                  Para una pausa: ficha salida al parar y entrada (Reanudar) al volver.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {settingsEditable && settingsOpen && (
        <div className="card shadow-sm mb-3">
          <div className="card-body">
            <h5>Auto-fichaje</h5>
            <form className="row g-2" onSubmit={saveSettings}>
              <div className="col-12">
                <div className="form-check">
                  <input
                    id="auto-enabled"
                    className="form-check-input"
                    type="checkbox"
                    checked={settings.autoEnabled}
                    onChange={(e) => setSettings((p) => ({ ...p, autoEnabled: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="auto-enabled">Activar auto-fichaje</label>
                </div>
              </div>
              <div className="col-md-3">
                <label className="form-label">Entrada desde</label>
                <input type="time" className="form-control" value={settings.inStart} onChange={(e) => setSettings((p) => ({ ...p, inStart: e.target.value }))} required />
              </div>
              <div className="col-md-3">
                <label className="form-label">Entrada hasta</label>
                <input type="time" className="form-control" value={settings.inEnd} onChange={(e) => setSettings((p) => ({ ...p, inEnd: e.target.value }))} required />
              </div>
              <div className="col-md-3">
                <label className="form-label">Salida desde</label>
                <input type="time" className="form-control" value={settings.outStart} onChange={(e) => setSettings((p) => ({ ...p, outStart: e.target.value }))} required />
              </div>
              <div className="col-md-3">
                <label className="form-label">Salida hasta</label>
                <input type="time" className="form-control" value={settings.outEnd} onChange={(e) => setSettings((p) => ({ ...p, outEnd: e.target.value }))} required />
              </div>
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <label className="form-label mb-0">Pausas</label>
                  {settings.pauses.length < MAX_PAUSES && (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => setSettings((p) => ({ ...p, pauses: [...p.pauses, emptyPause()] }))}
                    >
                      + Añadir pausa
                    </button>
                  )}
                </div>
                <div className="text-muted small mb-1">
                  Cada pausa ficha una salida (dentro de su ventana de inicio) y una nueva entrada (dentro de su ventana de vuelta), con hora aleatoria por trabajador y día, igual que la entrada y la salida.
                </div>
                {settings.pauses.length === 0 && (
                  <div className="text-muted small fst-italic">Sin pausas: el auto-fichaje registra la jornada de un solo tramo.</div>
                )}
                {settings.pauses.map((pause, idx) => (
                  <div className="row g-2 align-items-end mb-1" key={idx}>
                    <div className="col-auto pt-3 text-muted small">Pausa {idx + 1}</div>
                    <div className="col-md-2">
                      <label className="form-label small mb-0">Inicio desde</label>
                      <input
                        type="time" className="form-control form-control-sm" required
                        value={pause.outStart}
                        onChange={(e) => setSettings((p) => ({ ...p, pauses: p.pauses.map((it, i) => (i === idx ? { ...it, outStart: e.target.value } : it)) }))}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small mb-0">Inicio hasta</label>
                      <input
                        type="time" className="form-control form-control-sm" required
                        value={pause.outEnd}
                        onChange={(e) => setSettings((p) => ({ ...p, pauses: p.pauses.map((it, i) => (i === idx ? { ...it, outEnd: e.target.value } : it)) }))}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small mb-0">Vuelta desde</label>
                      <input
                        type="time" className="form-control form-control-sm" required
                        value={pause.inStart}
                        onChange={(e) => setSettings((p) => ({ ...p, pauses: p.pauses.map((it, i) => (i === idx ? { ...it, inStart: e.target.value } : it)) }))}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label small mb-0">Vuelta hasta</label>
                      <input
                        type="time" className="form-control form-control-sm" required
                        value={pause.inEnd}
                        onChange={(e) => setSettings((p) => ({ ...p, pauses: p.pauses.map((it, i) => (i === idx ? { ...it, inEnd: e.target.value } : it)) }))}
                      />
                    </div>
                    <div className="col-auto">
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        title="Quitar pausa"
                        onClick={() => setSettings((p) => ({ ...p, pauses: p.pauses.filter((_, i) => i !== idx) }))}
                      >
                        Quitar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="col-md-4">
                <label className="form-label">Zona horaria</label>
                <input className="form-control" value={settings.timezone} onChange={(e) => setSettings((p) => ({ ...p, timezone: e.target.value }))} />
              </div>
              <div className="col-md-8">
                <label className="form-label d-block">Días</label>
                <div className="d-flex flex-wrap gap-3">
                  {weekdayOptions.map((day) => (
                    <div className="form-check" key={day.value}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`weekday-${day.value}`}
                        checked={settings.weekdays.includes(day.value)}
                        onChange={(e) => {
                          setSettings((prev) => {
                            const next = e.target.checked
                              ? [...prev.weekdays, day.value]
                              : prev.weekdays.filter((d) => d !== day.value);
                            return { ...prev, weekdays: [...new Set(next)].sort((a, b) => a - b) };
                          });
                        }}
                      />
                      <label className="form-check-label" htmlFor={`weekday-${day.value}`}>{day.label}</label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-12">
                <button className="btn btn-primary" disabled={savingSettings} type="submit">
                  {savingSettings ? 'Guardando...' : 'Guardar auto-fichaje'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-md-2">
              <label className="form-label">Desde</label>
              <input type="date" className="form-control" value={filters.from} onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))} />
            </div>
            <div className="col-md-2">
              <label className="form-label">Hasta</label>
              <input type="date" className="form-control" value={filters.to} onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Usuario</label>
              <select className="form-select" value={filters.user_id} onChange={(e) => setFilters((p) => ({ ...p, user_id: e.target.value }))}>
                <option value="">Todos</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name || u.email}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Estado</label>
              <select className="form-select" value={filters.status} onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}>
                <option value="">Todos</option>
                <option value="propuesta">Sin confirmar</option>
                <option value="confirmada">Confirmadas</option>
              </select>
            </div>
            <div className="col-md-2 text-muted small">
              Registros: {pageInfo.total}<br />
              Total horas: <strong>{minutesToHm(totals.workedMinutes)}</strong>
            </div>
          </div>
          <div className="d-flex gap-2 mt-3">
            <button className="btn btn-outline-primary btn-sm" disabled={exporting === 'csv'} onClick={() => exportRegistry('csv')}>
              {exporting === 'csv' ? 'Generando...' : 'Exportar CSV'}
            </button>
            <button className="btn btn-outline-primary btn-sm" disabled={exporting === 'pdf'} onClick={() => exportRegistry('pdf')}>
              {exporting === 'pdf' ? 'Generando...' : 'Exportar PDF'}
            </button>
            <span className="text-muted small align-self-center">Exporta el registro filtrado (entrega al trabajador / Inspección).</span>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          {loading ? <p>Cargando jornadas...</p> : (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Usuario</th>
                    <th>Sesiones</th>
                    <th>Horas</th>
                    <th>Origen</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.length === 0 ? (
                    <tr><td colSpan="7">Sin registros.</td></tr>
                  ) : entries.map((item) => {
                    const isOwn = currentUserId != null && item.user_id === currentUserId;
                    const canEdit = isOwn || settingsEditable;
                    return (
                    <tr key={item.id}>
                      <td>{fmtDate(item.work_date)}</td>
                      <td>{item.user_name || item.user_email || `Usuario ${item.user_id}`}</td>
                      <td>{sessionsSummary(item)}</td>
                      <td>{minutesToHm(item.worked_minutes)}</td>
                      <td>{(item.in_source === 'auto' || item.out_source === 'auto') ? 'Automático' : 'Manual'}</td>
                      <td>
                        {item.status === 'confirmada' ? (
                          <span className="badge bg-success" title={item.confirmed_by_name ? `Confirmada por ${item.confirmed_by_name}` : 'Confirmada'}>Confirmada</span>
                        ) : (
                          <span className="badge bg-warning text-dark">Sin confirmar</span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-1 flex-wrap">
                          {isOwn && item.status === 'propuesta' && item.clock_in_at && Number(item.open_count || 0) === 0 && (
                            <button className="btn btn-sm btn-success" disabled={rowBusy === `confirm-${item.id}`} onClick={() => confirmEntry(item)}>
                              {rowBusy === `confirm-${item.id}` ? '...' : 'Confirmar'}
                            </button>
                          )}
                          {canEdit && (
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => openCorrection(item)}>Corregir</button>
                          )}
                          <button className="btn btn-sm btn-outline-info" onClick={() => openAudit(item)}>Historial</button>
                        </div>
                      </td>
                    </tr>
                  ); })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {correcting && (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <form onSubmit={submitCorrection}>
                <div className="modal-header">
                  <h5 className="modal-title">Corregir jornada · {fmtDate(correcting.work_date)}</h5>
                  <button type="button" className="btn-close" onClick={() => setCorrecting(null)}></button>
                </div>
                <div className="modal-body">
                  <label className="form-label">Sesiones del día (cada tramo de trabajo; las pausas son los huecos entre ellos)</label>
                  {correctForm.sessions.map((s, idx) => (
                    <div className="row g-2 align-items-end mb-2" key={idx}>
                      <div className="col-sm-5">
                        <label className="form-label small mb-0">Entrada {idx + 1} <span className="text-danger">*</span></label>
                        <input type="datetime-local" className="form-control" required value={s.clockInAt} onChange={(e) => updateSession(idx, 'clockInAt', e.target.value)} />
                      </div>
                      <div className="col-sm-5">
                        <label className="form-label small mb-0">Salida {idx + 1}</label>
                        <input type="datetime-local" className="form-control" value={s.clockOutAt} onChange={(e) => updateSession(idx, 'clockOutAt', e.target.value)} />
                      </div>
                      <div className="col-sm-2">
                        <button type="button" className="btn btn-outline-danger btn-sm w-100" disabled={correctForm.sessions.length <= 1} onClick={() => removeSession(idx)}>Quitar</button>
                      </div>
                    </div>
                  ))}
                  <button type="button" className="btn btn-outline-secondary btn-sm mb-3" onClick={addSession}>+ Añadir sesión (pausa)</button>
                  <div className="form-text mb-3">Solo la última sesión puede quedar sin salida (jornada abierta). Las sesiones no pueden solaparse.</div>
                  <div className="mb-2">
                    <label className="form-label">Motivo de la corrección <span className="text-danger">*</span></label>
                    <textarea className="form-control" rows="2" required value={correctForm.reason} onChange={(e) => setCorrectForm((p) => ({ ...p, reason: e.target.value }))} placeholder="Ej.: olvidé fichar la salida real a las 18:00" />
                    <div className="form-text">Toda corrección queda registrada en el historial de auditoría.</div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setCorrecting(null)}>Cancelar</button>
                  <button type="submit" className="btn btn-primary" disabled={savingCorrection}>
                    {savingCorrection ? 'Guardando...' : 'Guardar corrección'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {auditFor && (
        <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Historial de cambios · {fmtDate(auditFor.work_date)}</h5>
                <button type="button" className="btn-close" onClick={() => setAuditFor(null)}></button>
              </div>
              <div className="modal-body">
                {loadingAudit ? <p className="mb-0">Cargando historial...</p> : (
                  auditItems.length === 0 ? <p className="mb-0 text-muted">Sin movimientos registrados.</p> : (
                    <div className="table-responsive">
                      <table className="table table-sm align-middle">
                        <thead>
                          <tr>
                            <th>Fecha</th>
                            <th>Campo</th>
                            <th>Antes</th>
                            <th>Después</th>
                            <th>Origen</th>
                            <th>Autor</th>
                            <th>Motivo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditItems.map((a) => (
                            <tr key={a.id}>
                              <td>{fmtDateTime(a.changed_at)}</td>
                              <td>{fieldLabel(a.field)}</td>
                              <td>{auditValue(a.field, a.old_value)}</td>
                              <td>{auditValue(a.field, a.new_value)}</td>
                              <td>{sourceLabel(a.change_source)}</td>
                              <td>{a.changed_by_name || (a.change_source === 'auto' ? 'Sistema' : '-')}</td>
                              <td>{a.reason || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setAuditFor(null)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <details className="mt-3">
        <summary className="text-muted small" style={{ cursor: 'pointer' }}>Información sobre protección de datos (RGPD)</summary>
        <div className="card shadow-sm mt-2">
          <div className="card-body small text-muted">
            <p className="mb-1"><strong>Responsable del tratamiento:</strong> [tu empresa]. <strong>Finalidad:</strong> registro diario de la jornada laboral de cada persona trabajadora.</p>
            <p className="mb-1"><strong>Base jurídica:</strong> cumplimiento de una obligación legal (art. 6.1.c RGPD y art. 34.9 del Estatuto de los Trabajadores); el tratamiento no se basa en el consentimiento.</p>
            <p className="mb-1"><strong>Destinatarios:</strong> la dirección de la empresa, la representación legal de las personas trabajadoras y la Inspección de Trabajo y Seguridad Social.</p>
            <p className="mb-1"><strong>Conservación:</strong> los registros se conservan durante 4 años.</p>
            <p className="mb-0"><strong>Derechos:</strong> acceso, rectificación, supresión, limitación y oposición, dirigiéndose a [contacto del responsable / DPO]. Puedes reclamar ante la Agencia Española de Protección de Datos (aepd.es).</p>
          </div>
        </div>
      </details>
    </div>
  );
};

export default WorkdayPage;
